/*
 * BulkImport WebSocket Service
 */

import { io } from 'socket.io-client';

class BulkImportWebSocket {
  constructor() {
    this.socket = null;
    this.eventCallbacks = new Map();
    this.isConnected = false;
    this.userId = null;
  }

  // Connect to bulk import WebSocket namespace
  connect(userId, callbacks = {}) {
    if (this.socket && this.isConnected) {
      return Promise.resolve();
    }

    this.userId = userId;
    this.eventCallbacks = new Map(Object.entries(callbacks));

    const socketUrl =
      process.env.REACT_APP_WEBSOCKET_URL || window.location.origin;

    return new Promise((resolve, reject) => {
      try {
        this.socket = io(`${socketUrl}/bulk-import`, {
          query: { userId },
          transports: ['websocket', 'polling'], // Allow fallback to polling
          timeout: 10000, // Increase timeout
          forceNew: true, // Force new connection
          reconnection: true,
          reconnectionAttempts: 3,
          reconnectionDelay: 1000,
        });

        // Connection events
        this.socket.on('connect', () => {
          this.isConnected = true;
          this.emit('connected');
          resolve();
        });

        this.socket.on('disconnect', reason => {
          this.isConnected = false;
          this.emit('disconnected', { reason });
        });

        this.socket.on('connect_error', error => {
          this.isConnected = false;
          console.warn('WebSocket connection error:', error.message);
          this.emit('error', { error: error.message });

          // Don't reject immediately, let it retry
          setTimeout(() => {
            if (!this.isConnected) {
              reject(
                new Error(`Failed to connect to WebSocket: ${error.message}`),
              );
            }
          }, 2000);
        });

        // Bulk import events
        this.socket.on('import_started', data => {
          this.emit('jobStarted', data);
        });

        this.socket.on('import_progress', data => {
          this.emit('jobProgress', data);
        });

        this.socket.on('import_completed', data => {
          this.emit('jobCompleted', data);
        });

        this.socket.on('import_failed', data => {
          this.emit('jobFailed', data);
        });

        // Generic error handling
        this.socket.on('error', error => {
          this.emit('error', { error });
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  // Disconnect from WebSocket
  disconnect() {
    if (this.socket) {
      try {
        this.socket.disconnect();
      } catch (error) {
        console.warn('Error during WebSocket disconnect:', error);
      }
      this.socket = null;
      this.isConnected = false;
      this.eventCallbacks.clear();
    }
  }

  // Emit event to registered callbacks
  emit(eventName, data = {}) {
    const callback = this.eventCallbacks.get(eventName);
    if (callback && typeof callback === 'function') {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in WebSocket callback for ${eventName}:`, error);
      }
    }
  }

  // Subscribe to events
  on(eventName, callback) {
    this.eventCallbacks.set(eventName, callback);
  }

  // Unsubscribe from events
  off(eventName) {
    this.eventCallbacks.delete(eventName);
  }

  // Get connection status
  getStatus() {
    return {
      isConnected: this.isConnected,
      userId: this.userId,
      socketId: this.socket?.id,
    };
  }

  // Rejoin rooms (useful for reconnection)
  rejoin() {
    if (this.socket && this.isConnected && this.userId) {
      this.socket.emit('join', { userId: this.userId });
    }
  }
}

// Create singleton instance
const bulkImportWebSocket = new BulkImportWebSocket();

export default bulkImportWebSocket;
